import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, shareReplay, take, tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { NavigationEnd, Router } from '@angular/router';
import { TenantApi, ExternalTaskApi } from '@models/RutersApi';
import { ThemeConfig } from '@models/Tenant/theme.model';
import { ImgConfiguratonModel, CssConfigurationModel } from '@models/Tenant/theme.model';
import { UserService } from '@services/user/user.service';
import { ToastrService } from '@services/user/toastr.service';
import { LanguageService } from '@services/user/language.service';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private imgConfigurationMapSubject = new BehaviorSubject<ImgConfiguratonModel[] | null>(null);
  public imgConfigurationMap$ = this.imgConfigurationMapSubject
    .asObservable()
    .pipe(filter((conf: any) => conf != null));
  private themeLoadedSubject = new BehaviorSubject<boolean>(false);
  themeLoaded$ = this.themeLoadedSubject.asObservable();

  private themeRequest$?: Observable<any>;
  private externalThemeRequest$?: Observable<any>;
  private externalThemeTenant?: string;
  private theme: any;

  private readonly themeCatch = {
    cssConfiguration: [],
    imgConfiguration: [
      {
        name: 'menuLogo',
        base64: environment['envType'] == 'onprem-eni' ? '/assets/img/logo-eni.svg' : '/assets/img/logo.svg',
      },
      {
        name: 'loadinglogo',
        base64: environment['envType'] == 'onprem-eni' ? '/assets/img/logo-eni.svg' : '/assets/img/logo.svg',
      },
    ],
  };

  constructor(
    private http: HttpClient,
    private router: Router,
    private userService: UserService,
    private toastrService: ToastrService,
    private languageService: LanguageService,
  ) {
    this.loadEnvCss();
  }

  private onThemeAvailable(theme: any) {
    let themeConfig: ThemeConfig;
    try {
      themeConfig = JSON.parse(theme);
    } catch (e) {
      themeConfig = this.themeCatch;
    }
    // fix loading logo not found
    if (!themeConfig.imgConfiguration.find(({ name }) => name == 'loadinglogo')?.base64) {
      themeConfig.imgConfiguration.push({ name: 'loadinglogo', base64: themeConfig.imgConfiguration.find(({ name }) => name == 'menuLogo')?.base64 || '' });
    }
    const favicon = themeConfig.imgConfiguration.find(({ name }) => name == 'favicon');
    if (favicon) {
      document.getElementById('appFavicon')?.setAttribute('href', favicon.base64);
    }
    themeConfig.cssConfiguration = themeConfig.cssConfiguration || (this.themeCatch.cssConfiguration as CssConfigurationModel[]);
    themeConfig.imgConfiguration = themeConfig.imgConfiguration || (this.themeCatch.imgConfiguration as ImgConfiguratonModel[]);

    this.imgConfigurationMapSubject.next(themeConfig.imgConfiguration);
    themeConfig.cssConfiguration.forEach((element) => {
      document.documentElement.style.setProperty(element.property, element.value);
    });
    this.themeLoadedSubject.next(true);
  }

  loadTheme() {
    this.router.events/*.pipe(filter((event) => event instanceof NavigationEnd))*/.subscribe((event: any) => {
      const url = event.urlAfterRedirects;
      if (!url) {
        return;
      }
      if (url && url.startsWith('/external')) {
        this.loadThemeExternal(url);
      } else if (url && url.startsWith('/pages')) {
        this.loadThemeInternal();
      } else {
        this.onThemeAvailable({});
        //console.log('Page without theme', url);
      }
    });
  }

  private getThemeOnce(data: any): Observable<any> {
    if (!this.themeRequest$) {
      this.themeRequest$ = this.http.get(TenantApi.GET_THEME_CONFIGURATION).pipe(
        tap((res: any) => {
          if (res.error_messages) {
            this.languageService
              .instantTranslate(res.error_messages, data.language, { tenant: data.tenant })
              .subscribe((translatedMessage) => {
                this.toastrService.notice(translatedMessage, undefined, 30000, 'error');
              });
          } else {
            this.theme = res.data.data;
            this.onThemeAvailable(this.theme);
          }
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.themeRequest$;
  }

  private getExternalThemeOnce(tenant: string): Observable<any> {
    if (!this.externalThemeRequest$ || this.externalThemeTenant !== tenant) {
      this.externalThemeTenant = tenant;
      this.externalThemeRequest$ = this.http
        .get(ExternalTaskApi.GET_THEME_CONFIGURATION.replace('{tenant}', tenant))
        .pipe(
          tap((theme: any) => {
            this.theme = theme.data.data;
            this.onThemeAvailable(this.theme);
          }),
          shareReplay({ bufferSize: 1, refCount: false }),
        );
    }
    return this.externalThemeRequest$;
  }

  private loadThemeInternal() {
    this.userService.getUserdata().pipe(take(1)).subscribe({
      next: (data: any) => {
        this.getThemeOnce(data).pipe(take(1)).subscribe();
      },
      error: (err) => console.error('Errore nel recupero userdata', err),
    });
  }

  private loadThemeExternal(url: string) {
    if (!url) return;
    const urlTree = this.router.parseUrl(url);
    let tenant = urlTree.queryParams['tenant'];

    if (!tenant && environment['envType'] == 'onprem-eni') {
      tenant = 'eni-1';
    } else if (!tenant) {
      return;
    }
    this.getExternalThemeOnce(tenant).pipe(take(1)).subscribe();
  }

  private loadEnvCss() {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `/assets/env/${environment.envType}.css`;
    document.head.appendChild(link);
  }
}
